import { z } from '../zod.js';
import { AddressValidationSchema } from '../shared/addressValidationSchema';
import { NormalizedEmailSchema } from '../shared/normalizedEmail';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { hasAtLeastOneField } from '../shared/refines';

const StrictAddressValidationSchema = AddressValidationSchema.strict();

const CustomerShippingUpdateValidationSchema = z
  .object({
    name: z.string().min(1).nullable().optional().meta({
      description: 'Shipping recipient name. Send `null` to clear.',
    }),
    phone: z.string().min(1).nullable().optional().meta({
      description: 'Shipping phone number. Send `null` to clear.',
    }),
    address: StrictAddressValidationSchema.nullable().optional().meta({
      description:
        'Shipping address. Patch semantics — only the subfields you supply are changed. Send `null` to clear the whole subdocument.',
    }),
  })
  .strict();

const customerUpdateBodyShape = {
  name: z.string().min(1).nullable().optional().meta({
    description: 'Customer full name. Send `null` to clear.',
  }),
  companyName: z.string().min(1).nullable().optional().meta({
    description: 'Customer company name. Send `null` to clear.',
  }),
  email: NormalizedEmailSchema.optional().meta({
    description:
      'Primary customer email address. Trimmed and lowercased before storage. Cannot be cleared via this endpoint.',
  }),
  phone: z.string().min(1).nullable().optional().meta({
    description: 'Customer phone number. Send `null` to clear.',
  }),
  billingEmail: NormalizedEmailSchema.nullable().optional().meta({
    description:
      'Billing email address. Can be different from the primary email. Trimmed and lowercased before storage. Send `null` to clear.',
  }),
  address: StrictAddressValidationSchema.nullable().optional().meta({
    description:
      'Billing address. Patch semantics — only the subfields you supply are changed. Send `null` to clear the whole subdocument.',
  }),
  shipping: CustomerShippingUpdateValidationSchema.nullable().optional().meta({
    description:
      'Shipping details. Patch semantics — only the fields you supply are changed (including nested `shipping.address` subfields). Send `null` to clear the whole subdocument.',
  }),
};

/**
 * The canonical list of fields a merchant may PATCH on a customer.
 * Derived from `customerUpdateBodyShape` so the API controller and the MCP
 * tool can iterate the same list without re-declaring it (drift-proof).
 */
export const CUSTOMER_UPDATE_FIELDS = Object.keys(customerUpdateBodyShape) as ReadonlyArray<
  keyof typeof customerUpdateBodyShape
>;

export const CustomerUpdateBodyValidationSchema = z
  .object(customerUpdateBodyShape)
  .strict()
  .refine(hasAtLeastOneField, {
    message: 'At least one field must be provided.',
  });

export const UpdateCustomerInputValidationSchema = z
  .object({
    customerId: ObjectIdStringValidationSchema.meta({
      description:
        'The ID of the customer to update. Use `list_customers` first if you need to discover the customer ID by name or email.',
    }),
    ...customerUpdateBodyShape,
  })
  .refine(
    value => {
      const { customerId: _customerId, ...rest } = value;
      return hasAtLeastOneField(rest);
    },
    { message: 'At least one update field must be provided.' },
  );
